import Loading from "components/Loading";
import PageHeader from "components/PageHeader";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchUpcomingHearings,
  HearingType,
  UpcomingHearing,
} from "services/caseServices";
import { Roles } from "types";
import { eyeIcon } from "utils/constant";
import { DateView, errorHandler, hasRole } from "utils/helper";

const ITEMS_PER_PAGE = 9;

// Tabs map to the backend `hearing_type` filter (omitted for "All").
const HEARING_TABS: { key: string; type?: HearingType }[] = [
  { key: "all" },
  { key: "charge", type: "charge" },
  { key: "appeal", type: "appeal" },
  { key: "bail", type: "bail" },
];

// Whole-day difference between today and the hearing date (negative = past).
const daysUntil = (date?: string | Date | null) => {
  if (!date) return null;
  const target = new Date(date);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const CountdownBadge = ({ date }: { date?: string | null }) => {
  const { t } = useTranslation();
  const remaining = daysUntil(date);
  if (remaining === null) return <>-</>;
  if (remaining > 0)
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
        ⏳ {remaining} {remaining === 1 ? t("day_left") : t("days_left")}
      </span>
    );
  if (remaining === 0)
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
        🔔 {t("ioc_hearing_today")}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
      ✓ {Math.abs(remaining)}{" "}
      {Math.abs(remaining) === 1 ? t("day_ago") : t("days_ago")}
    </span>
  );
};

const UpcomingHearingsList = () => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();

  const roles = loginData?.roles;
  const isProsecutor = hasRole(roles, [Roles.prosecutor]);

  // For non-prosecutor roles, resolve which role's crime types to filter by.
  // Mirrors the precedence used by the charge list (by-coordinator-role).
  const coordinatorRole = (() => {
    if (hasRole(roles, [Roles.zonalVariousChildWomenRegistrar]))
      return Roles.zonalVariousChildWomenRegistrar;
    if (hasRole(roles, [Roles.zonalEconomicCoruptionRegistrsrar]))
      return Roles.zonalEconomicCoruptionRegistrsrar;
    if (hasRole(roles, [Roles.variousDirector])) return Roles.variousDirector;
    if (hasRole(roles, [Roles.economicDirector])) return Roles.economicDirector;
    if (hasRole(roles, [Roles.corruptionDirector]))
      return Roles.corruptionDirector;
    if (hasRole(roles, [Roles.variousCoordinator]))
      return Roles.variousCoordinator;
    if (hasRole(roles, [Roles.economicCoordinator]))
      return Roles.economicCoordinator;
    if (hasRole(roles, [Roles.corruptionCoordinator]))
      return Roles.corruptionCoordinator;
    if (hasRole(roles, [Roles.childWomenCoordinator]))
      return Roles.childWomenCoordinator;
    if (hasRole(roles, [Roles.variousClerk])) return Roles.variousClerk;
    if (hasRole(roles, [Roles.economicClerk])) return Roles.economicClerk;
    if (hasRole(roles, [Roles.corruptionClerk])) return Roles.corruptionClerk;
    if (hasRole(roles, [Roles.childWomenClerk])) return Roles.childWomenClerk;
    if (hasRole(roles, [Roles.variousRegistrar])) return Roles.variousRegistrar;
    if (hasRole(roles, [Roles.economicRegistrar]))
      return Roles.economicRegistrar;
    if (hasRole(roles, [Roles.corruptionRegistrar]))
      return Roles.corruptionCoordinator;
    if (hasRole(roles, [Roles.clerk])) return Roles.coordinator;
    return undefined;
  })();

  const [hearings, setHearings] = useState<UpcomingHearing[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeKey, setActiveKey] = useState("all");

  const tabLabels: Record<string, string> = {
    all: t("all"),
    charge: t("charge_hearing"),
    appeal: t("appeal_hearing"),
    bail: t("bail_hearings"),
  };
  const tabs = HEARING_TABS.map((tb) => tabLabels[tb.key]);
  const activeType = HEARING_TABS.find((tb) => tb.key === activeKey)?.type;

  // Open the right detail page per hearing category (not always the charge).
  const openHearing = (h: any) => {
    const cat = (h?.category || h?.source || "").toString().toLowerCase();
    if (cat.includes("bail")) {
      navigate(`/bail-right/${h?.bail_right_id || h?.source_id || h?.case_id}`);
    } else if (cat.includes("cassation")) {
      navigate(
        `/cassation-appeal/${
          h?.appeal_request_id || h?.source_id || h?.case_id
        }`,
      );
    } else if (cat.includes("defendant")) {
      navigate(
        `/defendant-appeal/${
          h?.defendant_appeal_request_id || h?.source_id || h?.case_id
        }`,
      );
    } else if (cat.includes("appeal")) {
      navigate(
        `/prosecutor-appeal/${
          h?.appeal_request_id || h?.source_id || h?.case_id
        }`,
      );
    } else {
      navigate(`/charges/${h?.case_id}`);
    }
  };

  // Two filtering modes by role:
  //  - Prosecutor:                scoped to their own userid
  //  - Coordinator/Director/      scoped to their role's crime types in their
  //    Registrar/Clerk            admin unit (coordinator_role + admin_unit_id)
  // Both are narrowed further by the selected hearing_type tab.
  const scope = isProsecutor
    ? { assigned_prosecutor_userid: loginData?.user_id || "" }
    : coordinatorRole
    ? {
        coordinator_role: coordinatorRole,
        admin_unit_id: loginData?.admin_unit || "",
      }
    : {};

  useEffect(() => {
    if (!loginData) return;
    setLoading(true);
    fetchUpcomingHearings({
      hearing_type: activeType,
      ...scope,
      page: currentPage,
      pageSize: ITEMS_PER_PAGE,
    })
      .then((data) => {
        setHearings(data.results || []);
        setCount(data.count || 0);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setHearings([]);
        setCount(0);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginData, currentPage, activeType, isProsecutor, coordinatorRole]);

  const totalPages = Math.ceil(count / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("upcoming_hearings")}
        icon={FaCalendarAlt}
        tabs={tabs}
        activeTab={tabLabels[activeKey]}
        onTabClick={(tab: string) => {
          const found = HEARING_TABS.find((tb) => tabLabels[tb.key] === tab);
          setActiveKey(found?.key || "all");
          setCurrentPage(1);
        }}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th className="p-3 text-left">{t("registration_number")}</th>
                  <th className="p-3 text-left">{t("category")}</th>
                  <th className="p-3 text-left">{t("appointment_date")}</th>
                  <th className="p-3 text-left">{t("days_left")}</th>
                  <th className="p-3 text-left">{t("court")}</th>
                  <th className="p-3 text-left">{t("prosecutor")}</th>
                  <th className="p-3 text-left">{t("appointment")}</th>
                  <th className="p-3 text-left">{t("view")}</th>
                </tr>
              </thead>
              <tbody>
                {hearings.map((h, idx) => (
                  <tr
                    key={h?.id ?? idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {h?.registration_number || "-"}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {h?.category ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {t(`hearing_category_${h.category.toLowerCase()}`, {
                            defaultValue: h.category,
                          })}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {DateView(h?.appointment_date)}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      <CountdownBadge date={h?.appointment_date} />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {[h?.court_type, h?.court_level].filter(Boolean).join(" ")}
                      {h?.court_registration_number
                        ? ` (${h?.court_registration_number})`
                        : ""}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {h?.assigned_prosecutor_name || "-"}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {h?.appointment_type || "-"}
                      {h?.appointment_reason ? ` — ${h?.appointment_reason}` : ""}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => openHearing(h)}
                      >
                        {eyeIcon}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && hearings.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-gray-500">
                      {t("no_hearings_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("prev")}
            </button>
            <span className="px-2">
              {t("page")} {currentPage} {t("of")} {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingHearingsList;
